/**
 * Families: who a student lives with, who may do what for them, and how to
 * reach them.
 *
 * A contact is a person (a `guardian` document, whatever their relationship).
 * A household is where people live. The relationship between one contact
 * and one student (`studentGuardian`) carries the rights — explicitly, one
 * by one, with dates — because the label never decides them: "Mother" does
 * not mean portal access, an emergency contact is not a pickup adult, portal
 * access is not the right to sign a form. A restriction always wins over a
 * right. One contact with three children has three relationships and one
 * account.
 *
 * The older booleans on `studentGuardian` (hasPortalAccess, canPickUp,
 * isFinanciallyResponsible, receives*) are kept in step from the rights so
 * every existing reader keeps working; a relationship written before rights
 * existed has its rights derived from them.
 */

import type { BaseDocument } from '../platform/documents.js'
import type { PostalAddress, StudentGuardian } from './index.js'

export const FAMILY_RIGHTS = [
  'portal',
  'educationRecords',
  'routineCommunication',
  'emergencyCommunication',
  'emergencyContact',
  'pickup',
  'financialView',
  'financialResponsibility',
  'formsComplete',
  'formsSign',
  'transportationChange',
  'dismissalChange',
  'attendanceSubmit',
  'healthCommunication',
  'specialPrograms',
] as const
export type FamilyRight = (typeof FAMILY_RIGHTS)[number]
export type FamilyRights = Record<FamilyRight, boolean>

export const FAMILY_RIGHT_LABELS: Record<FamilyRight, string> = {
  portal: 'myEdu portal access',
  educationRecords: 'Education records (grades, attendance, schedule)',
  routineCommunication: 'Routine communication',
  emergencyCommunication: 'Emergency communication',
  emergencyContact: 'Emergency contact',
  pickup: 'Pickup / release',
  financialView: 'See fees and balances',
  financialResponsibility: 'Financially responsible',
  formsComplete: 'Complete forms',
  formsSign: 'Sign forms and consents',
  transportationChange: 'Request transportation changes',
  dismissalChange: 'Request dismissal changes',
  attendanceSubmit: 'Report absences',
  healthCommunication: 'Health communication',
  specialPrograms: 'Special programs (IEP / 504 / EL) communication',
}

export const RIGHT_GROUPS: { label: string; rights: FamilyRight[] }[] = [
  { label: 'Access', rights: ['portal', 'educationRecords'] },
  { label: 'Communication', rights: ['routineCommunication', 'emergencyCommunication', 'healthCommunication', 'specialPrograms'] },
  { label: 'Safety', rights: ['emergencyContact', 'pickup'] },
  { label: 'Money', rights: ['financialView', 'financialResponsibility'] },
  { label: 'Forms and requests', rights: ['formsComplete', 'formsSign', 'transportationChange', 'dismissalChange', 'attendanceSubmit'] },
]

export const NO_RIGHTS: FamilyRights = Object.fromEntries(FAMILY_RIGHTS.map((r) => [r, false])) as FamilyRights
const ALL_RIGHTS: FamilyRights = Object.fromEntries(FAMILY_RIGHTS.map((r) => [r, true])) as FamilyRights

export const RELATIONSHIP_TYPES = ['mother', 'father', 'parent', 'stepmother', 'stepfather', 'stepparent', 'grandmother', 'grandfather', 'grandparent', 'aunt', 'uncle', 'sibling', 'foster_parent', 'legal_guardian', 'caseworker', 'family_friend', 'neighbor', 'childcare_provider', 'other'] as const
export type RelationshipType = (typeof RELATIONSHIP_TYPES)[number]
export const RELATIONSHIP_TYPE_LABELS: Record<RelationshipType, string> = { mother: 'Mother', father: 'Father', parent: 'Parent', stepmother: 'Stepmother', stepfather: 'Stepfather', stepparent: 'Stepparent', grandmother: 'Grandmother', grandfather: 'Grandfather', grandparent: 'Grandparent', aunt: 'Aunt', uncle: 'Uncle', sibling: 'Sibling (adult)', foster_parent: 'Foster parent', legal_guardian: 'Legal guardian', caseworker: 'Caseworker', family_friend: 'Family friend', neighbor: 'Neighbor', childcare_provider: 'Childcare provider', other: 'Other' }

/** Reads a free-text relationship (as older records hold it) into a type. */
export function relationshipTypeOf(text: string | null | undefined): RelationshipType {
  const t = (text ?? '').toLowerCase().replace(/[^a-z]/g, '')
  const hit = RELATIONSHIP_TYPES.find((r) => r.replace(/_/g, '') === t)
  if (hit) return hit
  if (/^mom|^mum/.test(t)) return 'mother'
  if (/^dad/.test(t)) return 'father'
  if (/grand/.test(t)) return 'grandparent'
  if (/step/.test(t)) return 'stepparent'
  if (/foster/.test(t)) return 'foster_parent'
  if (/guardian/.test(t)) return 'legal_guardian'
  return 'other'
}

/** The legal standing, separate from the label: a father may hold no custody; a grandmother may be the legal guardian. */
export const LEGAL_STATUSES = ['parent_with_rights', 'legal_guardian', 'non_custodial_parent', 'custodian', 'none'] as const
export type LegalStatus = (typeof LEGAL_STATUSES)[number]
export const LEGAL_STATUS_LABELS: Record<LegalStatus, string> = { parent_with_rights: 'Parent with educational rights', legal_guardian: 'Legal guardian', non_custodial_parent: 'Non-custodial parent', custodian: 'Custodian (caregiver)', none: 'No legal standing' }

/** Starting points for the rights matrix. A preset fills the boxes; the boxes are what is saved. */
export const RIGHT_PRESETS = {
  full_guardian: { label: 'Parent / guardian — full rights', rights: ALL_RIGHTS },
  non_custodial_records: { label: 'Non-custodial parent — records and communication', rights: { ...NO_RIGHTS, portal: true, educationRecords: true, routineCommunication: true, emergencyCommunication: true, healthCommunication: true, specialPrograms: true, formsComplete: true } },
  household_adult: { label: 'Household adult — pickup and communication', rights: { ...NO_RIGHTS, routineCommunication: true, emergencyCommunication: true, emergencyContact: true, pickup: true } },
  emergency_only: { label: 'Emergency contact only — no pickup', rights: { ...NO_RIGHTS, emergencyCommunication: true, emergencyContact: true } },
  emergency_pickup: { label: 'Emergency contact with pickup', rights: { ...NO_RIGHTS, emergencyCommunication: true, emergencyContact: true, pickup: true } },
  financial_only: { label: 'Financial responsibility only', rights: { ...NO_RIGHTS, financialView: true, financialResponsibility: true, routineCommunication: true } },
  caseworker: { label: 'Caseworker — records and communication', rights: { ...NO_RIGHTS, educationRecords: true, routineCommunication: true, emergencyCommunication: true, healthCommunication: true, specialPrograms: true } },
  none: { label: 'No rights (listed only)', rights: NO_RIGHTS },
} as const satisfies Record<string, { label: string; rights: FamilyRights }>
export type RightPreset = keyof typeof RIGHT_PRESETS

/** The relationship's rights, saved or (on an older record) derived from its booleans. */
export function rightsOf(link: Pick<StudentGuardian, 'hasPortalAccess' | 'canPickUp' | 'isFinanciallyResponsible' | 'receivesMailings' | 'receivesGradeNotifications' | 'receivesAttendanceNotifications' | 'isCustodial' | 'emergencyPriority'> & { rights?: Partial<FamilyRights> | null }): FamilyRights {
  if (link.rights) return { ...NO_RIGHTS, ...link.rights }
  const custodial = link.isCustodial !== false
  return {
    portal: Boolean(link.hasPortalAccess),
    educationRecords: Boolean(link.hasPortalAccess) || custodial,
    routineCommunication: Boolean(link.receivesMailings || link.receivesGradeNotifications || link.receivesAttendanceNotifications || link.hasPortalAccess),
    emergencyCommunication: true,
    emergencyContact: link.emergencyPriority != null,
    pickup: Boolean(link.canPickUp),
    financialView: Boolean(link.isFinanciallyResponsible || (link.hasPortalAccess && custodial)),
    financialResponsibility: Boolean(link.isFinanciallyResponsible),
    formsComplete: Boolean(link.hasPortalAccess),
    formsSign: Boolean(link.hasPortalAccess && custodial),
    transportationChange: Boolean(link.hasPortalAccess && custodial),
    dismissalChange: Boolean(link.hasPortalAccess && custodial),
    attendanceSubmit: Boolean(link.hasPortalAccess && custodial),
    healthCommunication: custodial,
    specialPrograms: custodial,
  }
}

/** What the older readers see, kept in step with the rights on every write. */
export function legacyFlagsFor(rights: FamilyRights, restricted: boolean) {
  return {
    hasPortalAccess: rights.portal && !restricted,
    canPickUp: rights.pickup && !restricted,
    isFinanciallyResponsible: rights.financialResponsibility,
    receivesMailings: rights.routineCommunication,
    receivesGradeNotifications: rights.routineCommunication && rights.educationRecords,
    receivesAttendanceNotifications: rights.routineCommunication && rights.educationRecords,
    hasContactRestriction: restricted,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Restrictions
// ─────────────────────────────────────────────────────────────────────────────

export const FAMILY_RESTRICTION_TYPES = ['no_contact', 'no_pickup', 'no_records', 'no_portal', 'supervised_only', 'address_confidential', 'other'] as const
export type FamilyRestrictionType = (typeof FAMILY_RESTRICTION_TYPES)[number]
export const FAMILY_RESTRICTION_TYPE_LABELS: Record<FamilyRestrictionType, string> = { no_contact: 'No contact', no_pickup: 'No pickup / release', no_records: 'No access to records', no_portal: 'No portal access', supervised_only: 'Supervised contact only', address_confidential: 'Student address confidential from this person', other: 'Other (see directive)' }

/** Which rights each restriction takes away. A restriction never grants anything. */
export const RESTRICTION_DENIES: Record<FamilyRestrictionType, readonly FamilyRight[]> = {
  no_contact: FAMILY_RIGHTS,
  no_pickup: ['pickup', 'dismissalChange', 'transportationChange'],
  no_records: ['portal', 'educationRecords', 'financialView', 'formsComplete', 'formsSign', 'healthCommunication', 'specialPrograms', 'routineCommunication'],
  no_portal: ['portal', 'formsComplete', 'formsSign', 'attendanceSubmit', 'transportationChange', 'dismissalChange'],
  supervised_only: ['pickup', 'dismissalChange'],
  address_confidential: [],
  other: [],
}

export interface FamilyRestriction extends BaseDocument {
  docType: 'familyRestriction'
  studentId: string
  /** The contact it applies to; null for a person who is not a contact (named only). */
  contactId: string | null
  personName: string
  type: FamilyRestrictionType
  /** What staff must do — shown to the office, dismissal and emergency staff. */
  directive: string
  /** Confidential: the legal detail (case number, order terms). Shown only to those who manage restrictions. */
  detail: string | null
  source: 'court_order' | 'custody_agreement' | 'protective_order' | 'administrative' | 'other'
  effectiveStart: string
  effectiveEnd: string | null
  documentIds: string[]
  status: 'active' | 'ended'
  enteredByName: string
  verifiedByName: string | null
  endedReason: string | null
}

export const restrictionActive = (r: Pick<FamilyRestriction, 'status' | 'effectiveStart' | 'effectiveEnd'>, date: string) =>
  r.status === 'active' && r.effectiveStart <= date && (!r.effectiveEnd || r.effectiveEnd >= date)

/** The relationship in effect on a date: its dates, its rights, then every restriction. Deny wins. */
export function effectiveRights(link: Parameters<typeof rightsOf>[0] & { effectiveStart?: string | null; effectiveEnd?: string | null; status?: string | null; hasContactRestriction?: boolean }, restrictions: Pick<FamilyRestriction, 'type' | 'status' | 'effectiveStart' | 'effectiveEnd'>[], date: string): { rights: FamilyRights; denied: { right: FamilyRight; by: FamilyRestrictionType }[]; inEffect: boolean } {
  const inEffect = (link.status ?? 'active') === 'active' && (!link.effectiveStart || link.effectiveStart <= date) && (!link.effectiveEnd || link.effectiveEnd >= date)
  if (!inEffect) return { rights: NO_RIGHTS, denied: [], inEffect: false }
  const rights = { ...rightsOf(link) }
  const denied: { right: FamilyRight; by: FamilyRestrictionType }[] = []
  const active = restrictions.filter((r) => restrictionActive(r, date))
  // An older record flagged restricted with no restriction document: treat as no portal and no pickup until one is recorded.
  if (link.hasContactRestriction && !link.rights && !active.length) active.push({ type: 'no_portal', status: 'active', effectiveStart: '0000-01-01', effectiveEnd: null }, { type: 'no_pickup', status: 'active', effectiveStart: '0000-01-01', effectiveEnd: null })
  for (const r of active) for (const right of RESTRICTION_DENIES[r.type]) if (rights[right]) { rights[right] = false; denied.push({ right, by: r.type }) }
  return { rights, denied, inEffect: true }
}

// ─────────────────────────────────────────────────────────────────────────────
// Households, contact methods, preferences
// ─────────────────────────────────────────────────────────────────────────────

export interface HouseholdMember extends BaseDocument {
  docType: 'householdMember'
  householdId: string
  memberType: 'student' | 'contact'
  memberId: string
  /** For a student: is this where they mainly live? A student can live in two households. */
  residence: 'primary' | 'secondary' | 'none'
  effectiveStart: string
  effectiveEnd: string | null
  status: 'active' | 'ended'
}

export const memberActive = (m: Pick<HouseholdMember, 'status' | 'effectiveStart' | 'effectiveEnd'>, date: string) =>
  m.status === 'active' && m.effectiveStart <= date && (!m.effectiveEnd || m.effectiveEnd >= date)

export interface AddressHistoryEntry {
  address: PostalAddress
  from: string
  to: string | null
  changedByName: string
  reason: string | null
}

export const FAMILY_CONTACT_METHOD_TYPES = ['mobile', 'home', 'work', 'email'] as const
export type FamilyContactMethodType = (typeof FAMILY_CONTACT_METHOD_TYPES)[number]
export interface FamilyContactMethod {
  id: string
  type: FamilyContactMethodType
  value: string
  primary: boolean
  canText: boolean
  /** Marked unreachable (bounced email, disconnected number). Never deleted: the history matters. */
  invalid: { at: string; reason: string; byName: string } | null
}

export const COMMUNICATION_CATEGORIES = ['emergency', 'attendance', 'academic', 'fees', 'general'] as const
export type CommunicationCategory = (typeof COMMUNICATION_CATEGORIES)[number]
export const COMMUNICATION_CATEGORY_LABELS: Record<CommunicationCategory, string> = { emergency: 'Emergency', attendance: 'Attendance', academic: 'Grades and academics', fees: 'Fees', general: 'General news' }
export type CommunicationChannel = 'email' | 'sms' | 'voice' | 'portal'
export interface CommunicationPreferences {
  language: string
  /** Per category, the channels the contact wants. Emergency is always delivered; it cannot be opted out of. */
  channels: Record<CommunicationCategory, CommunicationChannel[]>
}
export const DEFAULT_COMMUNICATION: CommunicationPreferences = { language: 'English', channels: { emergency: ['sms', 'voice', 'email'], attendance: ['sms', 'email'], academic: ['email', 'portal'], fees: ['email'], general: ['email'] } }

/** The contact methods a contact has, from the list or the older phone/email fields. */
export function contactMethodsOf(g: { contactMethods?: FamilyContactMethod[] | null; email?: string; phone?: string; mobilePhone?: string; workPhone?: string }): FamilyContactMethod[] {
  if (g.contactMethods?.length) return g.contactMethods
  const out: FamilyContactMethod[] = []
  if (g.mobilePhone) out.push({ id: 'cm-mobile', type: 'mobile', value: g.mobilePhone, primary: true, canText: true, invalid: null })
  if (g.phone && g.phone !== g.mobilePhone) out.push({ id: 'cm-home', type: 'home', value: g.phone, primary: !g.mobilePhone, canText: false, invalid: null })
  if (g.workPhone) out.push({ id: 'cm-work', type: 'work', value: g.workPhone, primary: false, canText: false, invalid: null })
  if (g.email) out.push({ id: 'cm-email', type: 'email', value: g.email, primary: true, canText: false, invalid: null })
  return out
}

/** The older single fields, kept in step with the list: the first valid of each kind. */
export function legacyContactFields(methods: FamilyContactMethod[]) {
  const valid = methods.filter((m) => !m.invalid)
  const pick = (t: FamilyContactMethodType) => (valid.find((m) => m.type === t && m.primary) ?? valid.find((m) => m.type === t))?.value
  return { email: pick('email'), mobilePhone: pick('mobile'), phone: pick('home') ?? pick('mobile'), workPhone: pick('work') }
}

export const normalizePhone = (p: string) => p.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')

/**
 * How alike two contacts are, for duplicate detection. Names alone are never
 * enough to merge — two Maria Garcias are two people — so a name match needs
 * a shared phone or email to score high.
 */
export function contactSimilarity(a: { firstName: string; lastName: string; emails: string[]; phones: string[] }, b: { firstName: string; lastName: string; emails: string[]; phones: string[] }): { score: number; reasons: string[] } {
  const reasons: string[] = []
  let score = 0
  const n = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '')
  const sameLast = n(a.lastName) === n(b.lastName) && n(a.lastName).length > 0
  const sameFirst = n(a.firstName) === n(b.firstName) && n(a.firstName).length > 0
  if (sameLast && sameFirst) { score += 40; reasons.push('Same name') } else if (sameLast && n(a.firstName)[0] === n(b.firstName)[0]) { score += 20; reasons.push('Similar name') }
  const ae = new Set(a.emails.map((e) => e.toLowerCase().trim()).filter(Boolean))
  if (b.emails.some((e) => ae.has(e.toLowerCase().trim()))) { score += 50; reasons.push('Same email') }
  const ap = new Set(a.phones.map(normalizePhone).filter((p) => p.length >= 7))
  if (b.phones.some((p) => ap.has(normalizePhone(p)))) { score += 35; reasons.push('Same phone') }
  return { score: Math.min(100, score), reasons }
}

export interface FamilyNote extends BaseDocument {
  docType: 'familyNote'
  householdId: string | null
  contactId: string | null
  studentId: string | null
  body: string
  authorName: string
  /** Confidential notes are shown only to those who manage restrictions. */
  confidential: boolean
}

export interface FamilyDocument extends BaseDocument {
  docType: 'familyDocument'
  householdId: string | null
  contactId: string | null
  studentId: string | null
  restrictionId: string | null
  kind: 'court_order' | 'custody_agreement' | 'guardianship' | 'proof_of_residence' | 'other'
  fileName: string
  contentType: string
  blobPath: string
  size: number | null
  uploadedByName: string
  status: 'pending' | 'stored' | 'removed'
  confidential: boolean
}

export interface FamilySettings extends BaseDocument {
  docType: 'familySettings'
  /** Which preset a new relationship of this type starts from. */
  defaultPresetByType: Partial<Record<RelationshipType, RightPreset>>
  requireEmergencyContacts: number
  requirePickupAdult: boolean
  duplicateThreshold: number
  version: number
  /** Set once the older households and links have been read into memberships. */
  migratedAt: string | null
}

export const DEFAULT_FAMILY_SETTINGS: Omit<FamilySettings, keyof BaseDocument> & { docType: 'familySettings' } = {
  docType: 'familySettings',
  defaultPresetByType: { mother: 'full_guardian', father: 'full_guardian', parent: 'full_guardian', legal_guardian: 'full_guardian', foster_parent: 'full_guardian', stepmother: 'household_adult', stepfather: 'household_adult', stepparent: 'household_adult', grandmother: 'emergency_only', grandfather: 'emergency_only', grandparent: 'emergency_only', aunt: 'emergency_only', uncle: 'emergency_only', caseworker: 'caseworker', family_friend: 'emergency_only', neighbor: 'emergency_only', childcare_provider: 'emergency_pickup' },
  requireEmergencyContacts: 2,
  requirePickupAdult: true,
  duplicateThreshold: 70,
  version: 0,
  migratedAt: null,
}

export function presetFor(settings: Pick<FamilySettings, 'defaultPresetByType'>, type: RelationshipType): RightPreset {
  return settings.defaultPresetByType[type] ?? 'none'
}

/** What a student's family setup is missing, for the audit report and the Student 360 card. */
export function familyGaps(links: { rights: FamilyRights; inEffect: boolean; contactName: string; methods: FamilyContactMethod[] }[], settings: Pick<FamilySettings, 'requireEmergencyContacts' | 'requirePickupAdult'>): string[] {
  const live = links.filter((l) => l.inEffect)
  const gaps: string[] = []
  const em = live.filter((l) => l.rights.emergencyContact).length
  if (em < settings.requireEmergencyContacts) gaps.push(`${em} emergency contact${em === 1 ? '' : 's'} (needs ${settings.requireEmergencyContacts})`)
  if (settings.requirePickupAdult && !live.some((l) => l.rights.pickup)) gaps.push('No one may pick up')
  if (!live.some((l) => l.rights.educationRecords)) gaps.push('No one holds education-record rights')
  for (const l of live.filter((x) => x.rights.emergencyContact || x.rights.emergencyCommunication)) if (!l.methods.some((m) => !m.invalid && m.type !== 'email')) gaps.push(`${l.contactName} has no working phone`)
  return gaps
}
